'use client'
import { useCartMiniQuery } from '@/hooks/react-query/cart/use-cart'
import { cn, formatPrice } from '@/lib/utils'
import { getCookie } from 'cookies-next'
import { ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { CartMiniItem, CartMiniItemSkeleton } from './cart-mini-item'

const START_SHOPPING_URL = '/collection/san-pham-moi'

export function CartMini() {
  // const { data: countCartItem, isLoading } = useCountCartItemQuery()
  const { data: cartMini, isLoading: isCartMiniLoading } = useCartMiniQuery({
    enabled: true, // Always enabled to show count badge
  })
  const [hasHovered, setHasHovered] = useState(false)
  const pathname = usePathname()

  const t = useTranslations('title')

  // Hide cart mini dropdown when on cart page
  const isCartPage = pathname?.includes('/cart')

  const handleMouseEnter = () => {
    if (!hasHovered && !isCartPage) {
      setHasHovered(true)
    }
  }

  return (
    <div
      className='group relative cursor-pointer'
      onMouseEnter={handleMouseEnter}
    >
      <Link
        href={'/cart'}
        aria-label={t('cart')}
        ga-tracking-value='navbar__cart'
        ga-tracking-label='cart'
      >
        <div className='relative flex h-13 w-13 cursor-pointer items-center justify-center'>
          <Image
            src={'/icons/icon-cart.svg'}
            alt='cart'
            width={24}
            height={24}
            className='h-6 w-6'
          />
          {/* Fake để nhận biết version mới */}
          <DetectVersionDot />
          {cartMini?.number_item ? (
            <span className='absolute bottom-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-error-500 text-xxs text-white'>
              {isCartMiniLoading ? '' : cartMini?.number_item}
            </span>
          ) : null}
        </div>
      </Link>

      {!isCartPage && (
        <div className="invisible absolute right-0 top-14 z-[999] w-[360px] translate-y-2 cursor-default rounded-lg bg-light opacity-0 shadow-[0px_1px_4px_0px_rgba(0,0,0,0.08),0px_0px_24px_0px_rgba(0,0,0,0.06)] transition-all duration-300 ease-out before:pointer-events-none before:absolute before:-top-4 before:right-4 before:h-0 before:w-0 before:border-[8px] before:border-solid before:border-transparent before:border-b-light before:content-[''] lg:group-hover:visible lg:group-hover:translate-y-0 lg:group-hover:opacity-100">
          <div className='p-4'>
            <CartMiniContent shouldLoad={hasHovered} />
          </div>
        </div>
      )}
    </div>
  )
}

const CartMiniContent = ({ shouldLoad }: { shouldLoad: boolean }) => {
  const { data: cartMini, isLoading } = useCartMiniQuery({
    enabled: shouldLoad, // Chỉ load khi đã hover
  })
  const t = useTranslations()

  const isCartEmpty = !cartMini?.items || cartMini.items.length === 0

  // Show skeleton while loading or haven't loaded yet
  if (!shouldLoad || isLoading) {
    return (
      <>
        <div className='mb-4 flex items-center justify-between'>
          <div className='h-4 w-40 animate-pulse rounded bg-neutral-200' />
          <div className='h-4 w-16 animate-pulse rounded bg-neutral-200' />
        </div>

        <div className='-mx-2 space-y-2'>
          {Array.from({ length: 3 }).map((_, index) => (
            <CartMiniItemSkeleton key={index} />
          ))}
        </div>
      </>
    )
  }

  if (isCartEmpty) {
    return (
      <div className='flex flex-col items-center gap-4'>
        <Image
          src='/icons/icon-cart-empty.svg'
          alt='empty cart'
          width={32}
          height={35}
        />
        <p className='text-xs text-neutral-900'>
          {t('header.cart_mini.title.empty')}
        </p>
        <Link
          href={START_SHOPPING_URL}
          className='flex items-center gap-1 rounded-full bg-neutral-900 px-4 pb-2 pt-1.5 font-criteria text-sm text-white hover:bg-neutral-800'
          ga-tracking-value='navbar__cart_mini__start_shopping'
          ga-tracking-label='navbar__cart_mini__start_shopping'
        >
          {t('header.cart_mini.title.start_shopping')}
          <ArrowRight size={14} />
        </Link>
      </div>
    )
  }

  return (
    <>
      <div className='mb-4 flex items-start justify-between whitespace-nowrap text-xs leading-4 text-neutral-900'>
        <div className='flex items-center gap-1'>
          <span className='opacity-60'>
            {t('header.cart_mini.title.estimated_price')}:
          </span>
          <span className='font-semibold'>
            {formatPrice(cartMini?.price ?? 0)}
          </span>
          <span className='opacity-60'>
            (
            {t('header.cart_mini.title.count_product', {
              count: cartMini?.number_item,
            })}
            )
          </span>
        </div>

        <Link
          href='/cart'
          className='text-primary-700 underline hover:text-primary-500'
        >
          {t('button.view_all')}
        </Link>
      </div>

      <div className='scrollbar -mx-2 max-h-[420px] space-y-2 overflow-y-auto'>
        {cartMini?.items.map((item, index) => (
          <CartMiniItem
            id={item.id}
            name={item.title}
            image={item.images.src}
            regularPrice={item.variants.regular_price}
            variantTitle={item.variants.title}
            quantity={item.quantity}
            link={'/cart'}
            key={`${item.id}-${index}`}
            variantId={item.variants.id}
            indexId={item.index_id}
          />
        ))}
      </div>

      <Link
        href='/cart'
        className='mx-auto mt-4 flex w-fit items-center gap-1 rounded-full border border-neutral-900 px-4 pb-2 pt-1.5 font-criteria text-sm text-neutral-900 transition-colors hover:bg-neutral-900 hover:text-white'
        ga-tracking-value='navbar__cart_mini__view_cart'
        ga-tracking-label='navbar__cart_mini__view_cart'
      >
        {t('header.cart_mini.title.view_cart')}
        <ArrowRight size={14} />
      </Link>
    </>
  )
}

const DetectVersionDot = () => {
  const [version, setVersion] = useState<string | undefined>(undefined)

  useEffect(() => {
    const v = getCookie('x-page-version') as string
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing state from cookie (external system) on mount
    setVersion(v)
  }, [])
  return (
    <span
      className={cn(
        'absolute bottom-1/2 right-1/2 flex h-[3px] w-[3px] translate-x-1/2 translate-y-1/3 items-center justify-center rounded-full bg-white',
        version === '2' && 'bg-error-500'
      )}
    ></span>
  )
}
